import { DateTime } from 'luxon';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRelayEnvironment } from 'react-relay';
import { v4 as uuidv4 } from 'uuid';

import EllipseContainer from '../components/ellipseContainer';
import ListAssigneeWithAvatar from '../components/listAssigneeWithAvatar';
import { FORMAT_DATE_DISPLAY_DAY_SHORT } from '../const';
import { getObjectFromRelayStoreById } from '../functions';
import { ScheduleItemFragment$data } from '../graphql/__generated__/ScheduleItemFragment.graphql';
import { ScheduleItemFragmentChildren$data } from '../graphql/__generated__/ScheduleItemFragmentChildren.graphql';
import { ScheduleItemsQuery$variables } from '../graphql/__generated__/ScheduleItemsQuery.graphql';
import { ScheduleItemConnectionFragment } from '../graphql/fragments/ScheduleItemConnectionFragment';
import { ScheduleItemFragment } from '../graphql/fragments/ScheduleItemFragment';
import { ScheduleItemListFragment } from '../graphql/fragments/ScheduleItemListFragment';
import CreateScheduleItemMutation from '../graphql/mutations/CreateScheduleItemMutation';
import DeleteScheduleItemMutation from '../graphql/mutations/DeleteScheduleItemMutation';
import UpdateScheduleItemMutation from '../graphql/mutations/UpdateScheduleItemMutation';
import { ScheduleItemsQuery } from '../graphql/ScheduleItemsQuery';
import useAccess from '../hooks/useAccess';
import { ListItem, ProjectSelect, UserSelect } from '../index';
import TableDb from '../outTableDb';
import { SmxGlobalFilterContext } from '../providers/globalFilterProvider';
import { SmxPermissionContext } from '../providers/permissionProvider';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { TaskBoardTaskAssignee } from '../taskBoard/component/taskBoardTaskAssignee';
import {
    ActionsType,
    Flatten,
    ItemType,
    ListItemType,
    MappingItemIdIndexType,
    OnlyObjectKeys,
    OnScheduleItemsImport,
    ProjectType,
} from '../types';
import GantItemProgress from './gantItemProgress';
import { GantTableCustomButtonEditView } from './gantTableCustomButtonEditView';

export interface IGantTable {
    onScheduleItemsImport?: OnScheduleItemsImport;
    gantEditScheduleItem?: {
        userId: string;
        projectId: string;
    }[];
    authorId: string;
}

const GantTable: React.FC<IGantTable> = ({ onScheduleItemsImport, gantEditScheduleItem, authorId }) => {
    const environment = useRelayEnvironment();
    const { userId } = useContext(SmxPermissionContext);
    const { filterProject } = useContext(SmxGlobalFilterContext);
    const dispatch = useDispatch<ComponentsDispatch>();

    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);
    const isItemEdit = useSelector<RootState, boolean>((state) => state.gantItems.isItemEdit);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const availableHeightTable = useSelector<RootState, number>((state) => state.gant.availableHeightTable);
    const updateScheduleItemFetchKey = useSelector<RootState, number>(
        (state) => state.setting.updateScheduleItemFetchKey,
    );
    const externalTableScheduleItemPrefixFetchKey = useSelector<RootState, string | undefined>(
        (state) => state.gantItems.externalTableScheduleItemPrefixFetchKey,
    );

    const isItemEditRef = useRef<boolean>(isItemEdit);
    const [fetchKey, setFetchKey] = useState(0);
    const [prefixFetchKey, setPrefixFetchKey] = useState('');

    isItemEditRef.current = isItemEdit;

    useEffect(() => {
        setFetchKey(fetchKey + 1);
    }, [updateScheduleItemFetchKey]);

    useEffect(() => {
        forceUpdateDataTable(externalTableScheduleItemPrefixFetchKey);
    }, [externalTableScheduleItemPrefixFetchKey]);

    const handleClickForEdit = (data: any) => {
        if (isItemEditRef.current) {
            setFetchKey((oldFetchKey) => ++oldFetchKey);
        }

        const interval = setInterval(() => {
            if (!isItemEditRef.current) {
                isItemEditRef.current = false;
                clearInterval(interval);
                console.log('finish editing item');
                setFetchKey((oldFetchKey) => ++oldFetchKey);
                setTimeout(() => {
                    setFetchKey(0);
                }, 1000);
                return;
            }
        }, 1000);

        const newCurrentGantItem = itemsList[mappingItemIdIndex[data.keyRaw]];

        console.log('click for edit', data, itemsList, mappingItemIdIndex, newCurrentGantItem);

        if (newCurrentGantItem) {
            dispatch(
                setterGantItems({ isItemEdit: true, currentGantItem: itemsList[mappingItemIdIndex[data.keyRaw]] }),
            );
        }
    };

    const listItemValue = (value: string) => {
        return <>{value ? getObjectFromRelayStoreById<ListItemType>(environment, value)?.name : ''}</>;
    };

    const filterFlexibleCommon = [];

    filterProject?.length && filterFlexibleCommon.push(`{"or":[{"project_id":"${filterProject.join('|')}"}]}`);

    const accessAll = useAccess(['work-item.all.view']);
    const accessOwner = useAccess(['work-item.owner.view']);
    const accessAssignee = useAccess(['work-item.assignee.view']);

    if (!accessAll) {
        const filterTaskOwnerAssignee = [];
        accessOwner && filterTaskOwnerAssignee.push(`{"owner_item_id":"${userId}"}`);
        accessAssignee && filterTaskOwnerAssignee.push(`{"assignee":"*${userId}"}`);

        (accessAssignee || accessOwner) && filterFlexibleCommon.push(`{"or":[${filterTaskOwnerAssignee.join(',')}]}`);
    }

    const filterFlexible = filterFlexibleCommon?.length ? `[${filterFlexibleCommon.join(',')}]` : undefined;

    const forceUpdateDataTable = (uuid?: string) => {
        setPrefixFetchKey(uuid || uuidv4());
    };

    const handleScheduleItemsImport: OnScheduleItemsImport = async (args) => {
        await onScheduleItemsImport?.(args);

        forceUpdateDataTable();
    };

    const access = useAccess(['work-item.all.create', 'work-item.all.edit', 'work-item.all.manage']);

    const getActions = (): Exclude<ActionsType, 'sorter' | 'filter'>[] => {
        const baseActions: Exclude<ActionsType, 'sorter' | 'filter'>[] = ['columnsView', 'export-csv'];

        if (access) {
            baseActions.push('import-csv');
        }
        return baseActions;
    };

    const filterFlexibleProject = filterProject?.length ? `[{"or":[{"id":"${filterProject.join('|')}"}]}]` : undefined;

    return (
        <TableDb<
            ScheduleItemsQuery$variables,
            Partial<Flatten<ScheduleItemFragmentChildren$data>> & Partial<Flatten<ScheduleItemFragment$data>>,
            OnlyObjectKeys<ScheduleItemFragmentChildren$data>
        >
            query={ScheduleItemsQuery}
            fragmentList={ScheduleItemListFragment}
            fragmentConnection={ScheduleItemConnectionFragment}
            fragment={[ScheduleItemFragment]}
            mutationCreate={CreateScheduleItemMutation}
            mutationUpdate={UpdateScheduleItemMutation}
            mutationDelete={DeleteScheduleItemMutation}
            nameQuery={'scheduleItems'}
            columnsLabel={{
                number_wbs: 'WBS',
                name: 'Name',
                start_datetime: 'Start Date',
                end_datetime: 'End Date',
                duration: 'Duration',
                completion_percentage: 'Progress %',
                type: 'Item Type',
                priority: 'Priority',
                project_id: 'Project',
                phase_id: 'Phase',
                division_id: 'Division',
                cost_code_id: 'Cost Code',
                count_people: 'People Count',
                value_total: 'Total Value',
                value_labour: 'Labour Value',
                value_other: 'Other Value',
                assignee: 'Assignee',
                owner_item_id: 'Owner',
            }}
            fixedColumn={{
                number_wbs: 'left',
                name: 'left',
                duration: 'left',
            }}
            defaultHiddenColumn={[
                'end_datetime',
                'phase_id',
                'division_id',
                'cost_code_id',
                'value_total',
                'value_labour',
                'value_other',
                'assignee',
                'owner_item_id',
            ]}
            paramFilter={{
                name: {},
                start_datetime: {},
                end_datetime: {},
                type: {
                    filterField: 'type',
                    filterComponent: <ListItem nameList={'schedule_item_type'} readonly />,
                    availableTypeFilter: ['from_list', 'has_any_value', 'is_unknown'],
                },
                project_id: {
                    filterField: 'project_id',
                    filterComponent: (
                        <ProjectSelect
                            filterList={filterProject ? [] : undefined}
                            filterFlexible={filterFlexibleProject}
                            // localFilters={{ id: filterProject }}
                        />
                    ),
                    availableTypeFilter: ['from_list', 'has_any_value', 'is_unknown'],
                },
                assignee: {
                    filterField: 'assignee', //'users.user_id',
                    filterComponent: <UserSelect />,
                    availableTypeFilter: ['from_list', 'has_any_value', 'is_unknown'],
                    useAsString: true,
                },
                owner_item_id: {
                    filterField: 'owner_item_id',
                    filterComponent: <UserSelect />,
                    availableTypeFilter: ['from_list', 'has_any_value', 'is_unknown'],
                },
                // 'statusItem.name': {
                //     filterField: 'status',
                //     filterComponent: <ListItem nameList={LIST_TASK_GROUP_STATUS} style={{ width: 200 }} />,
                // },
                // 'typeItem.name': {
                //     filterField: 'type',
                //     filterComponent: <ListItem nameList={LIST_TASK_TYPE} style={{ width: 200 }} />,
                // },
            }}
            renderDataColumn={{
                cost_code_id: listItemValue,
                phase_id: listItemValue,
                division_id: listItemValue,
                priority: listItemValue,
                type: listItemValue,
                name: (value: string) => {
                    return <EllipseContainer>{value}</EllipseContainer>;
                },
                project_id: (value: string) => {
                    return <>{value ? getObjectFromRelayStoreById<ProjectType>(environment, value)?.name : ''}</>;
                },
                start_datetime: (value: string) => {
                    return <>{value ? DateTime.fromSQL(value).toFormat(FORMAT_DATE_DISPLAY_DAY_SHORT) : ''}</>;
                },
                end_datetime: (value: string) => {
                    return <>{value ? DateTime.fromSQL(value).toFormat(FORMAT_DATE_DISPLAY_DAY_SHORT) : ''}</>;
                },
                completion_percentage: (value: string, data: any) => {
                    return (
                        <div style={{ paddingTop: 6 }}>
                            {
                                <GantItemProgress
                                    updatedItem={itemsList[mappingItemIdIndex[data.keyRaw]]}
                                    isHidePercentage={true}
                                    zIndex={1}
                                />
                            }
                        </div>
                    );
                },
                assignee: (value: string) => {
                    const listIdAssignee = value.split(',');

                    return <ListAssigneeWithAvatar listIdAssignee={listIdAssignee} />;
                },
                owner_item_id: (value: string) => {
                    return <TaskBoardTaskAssignee userId={value} noDisplayEmptyIcon displayHint />;
                },
            }}
            actions={getActions()}
            customActionComponents={[
                (item, data) => (
                    <GantTableCustomButtonEditView
                        data={data}
                        item={item as any}
                        onClick={handleClickForEdit}
                        gantEditScheduleItem={gantEditScheduleItem!}
                        authorId={authorId}
                    />
                ),
            ]}
            sizeOfPage={['10', '20', '50']}
            externalFetchKey={fetchKey}
            onClickRow={handleClickForEdit}
            // extendRow={['childrenList', 'childrenList', 'childrenList', 'childrenList', 'childrenList', 'childrenList']}
            // extendRowByField={[['tasks'], ['tasks'], ['tasks'], ['tasks'], ['tasks'], ['tasks']]}
            varRequest={{ order_by: ["project_id||COALESCE(number_wbs, ' '):ASC"], filter_flexible: filterFlexible }}
            heightTable={availableHeightTable}
            onSelect={(record) => console.log(record)}
            onSelectionChange={(selectedRowKeys) => console.log(selectedRowKeys)}
            onScheduleItemsImport={handleScheduleItemsImport}
            prefixFetchKey={prefixFetchKey}
        />
    );
};

export default GantTable;
